// src/pages/carbon-offset-dashboard/components/CarbonOffsetMetrics.jsx
import React from 'react';
import Icon from 'components/AppIcon';

const CarbonOffsetMetrics = ({ data, timeRange }) => {
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(num);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const calculatePercentageChange = (current, previous) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const getChangeIcon = (change) => {
    if (change > 0) return 'TrendingUp';
    if (change < 0) return 'TrendingDown';
    return 'Minus';
  };

  const getChangeColor = (change, isPositiveGood = false) => {
    if (change > 0) return isPositiveGood ? 'text-success' : 'text-error';
    if (change < 0) return isPositiveGood ? 'text-error' : 'text-success';
    return 'text-text-secondary';
  };

  const offsetPercentage = (data.offsetPurchased.current / data.totalEmissions.current) * 100;
  const remainingEmissions = data.totalEmissions.current - data.offsetPurchased.current;
  const estimatedOffsetCost = remainingEmissions * 20; // $20 per tonne estimate

  const emissionsChange = calculatePercentageChange(data.totalEmissions.current, data.totalEmissions.previous);
  const offsetChange = calculatePercentageChange(data.offsetPurchased.current, data.offsetPurchased.previous);
  const percentageChange = calculatePercentageChange(offsetPercentage, data.offsetPercentage.previous);

  const metrics = [
    {
      id: 'total-emissions',
      title: 'Total Emissions',
      value: formatNumber(data.totalEmissions.current),
      unit: data.totalEmissions.unit,
      change: emissionsChange,
      changeLabel: `${Math.abs(emissionsChange).toFixed(1)}% vs last period`,
      icon: 'Factory',
      iconColor: 'text-error',
      iconBg: 'bg-error-50',
      isPositiveGood: false
    },
    {
      id: 'offset-purchased',
      title: 'Offset Purchased',
      value: formatNumber(data.offsetPurchased.current),
      unit: data.offsetPurchased.unit,
      change: offsetChange,
      changeLabel: `${Math.abs(offsetChange).toFixed(1)}% vs last period`,
      icon: 'Leaf',
      iconColor: 'text-success',
      iconBg: 'bg-success-50',
      isPositiveGood: true
    },
    {
      id: 'offset-percentage',
      title: 'Offset Coverage',
      value: formatNumber(offsetPercentage),
      unit: '%',
      change: percentageChange,
      changeLabel: `${Math.abs(percentageChange).toFixed(1)}% vs last period`,
      icon: 'Shield',
      iconColor: 'text-primary',
      iconBg: 'bg-primary-50',
      isPositiveGood: true
    },
    {
      id: 'remaining-emissions',
      title: 'Remaining Emissions',
      value: formatNumber(remainingEmissions),
      unit: 'tonnes CO2e',
      change: null,
      changeLabel: `Est. ${formatCurrency(estimatedOffsetCost)} to offset`,
      icon: 'AlertTriangle',
      iconColor: 'text-warning',
      iconBg: 'bg-warning-50',
      isPositiveGood: false
    },
    {
      id: 'carbon-credits',
      title: 'Available Credits',
      value: formatNumber(data.carbonCredits.available),
      unit: data.carbonCredits.unit,
      change: null,
      changeLabel: `${formatNumber(data.carbonCredits.pending)} pending verification`,
      icon: 'Award',
      iconColor: 'text-secondary',
      iconBg: 'bg-secondary-50',
      isPositiveGood: true
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
      {metrics.map((metric) => (
        <div key={metric.id} className="bg-surface rounded-lg border border-border p-6 hover:shadow-md transition-shadow duration-150">
          <div className="flex items-center justify-between mb-4">
            <div className={`p-3 rounded-lg ${metric.iconBg}`}>
              <Icon name={metric.icon} size={24} className={metric.iconColor} />
            </div>
            {metric.change !== null && (
              <div className={`flex items-center space-x-1 ${getChangeColor(metric.change, metric.isPositiveGood)}`}>
                <Icon 
                  name={getChangeIcon(metric.change)} 
                  size={16} 
                />
                <span className="text-sm font-medium">
                  {Math.abs(metric.change).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
          
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">
              {metric.title}
            </h3>
            <div className="flex items-baseline space-x-2">
              <span className="text-2xl font-bold text-text-primary">
                {metric.value}
              </span>
              <span className="text-sm text-text-secondary font-medium">
                {metric.unit}
              </span>
            </div>
            <p className="text-xs text-text-secondary mt-2">
              {metric.changeLabel}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CarbonOffsetMetrics;